import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeriesCard from "@/components/feature/SeriesCard";
import Pagination from "@/components/feature/Pagination";
import { useSeo } from "@/hooks/useSeo";
import { seriesEras, seriesList } from "@/mocks/series";
import FilterPanel, { type FilterState } from "./components/FilterPanel";

const PAGE_SIZE = 8;

const defaultFilter: FilterState = {
  era: "",
  categories: [],
  regions: [],
  sort: "hot",
};

export default function Category() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const catParam = searchParams.get("cat");

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  useSeo({
    title: "仙剑奇侠传电视剧分类 - 影视剧集大全",
    description:
      "仙剑奇侠传电视剧分类页，支持按年代、副分类与地区多维筛选，按热度、首播时间与评分排序，快速找到你想看的仙侠剧集。",
    keywords: "仙剑奇侠传电视剧,仙侠剧分类,仙侠剧筛选,仙剑剧集大全,古装仙侠剧",
  });

  useEffect(() => {
    setFilter({ ...defaultFilter, categories: catParam ? [catParam] : [] });
    setPage(1);
  }, [catParam, query]);

  const filtered = useMemo(() => {
    const list = seriesList.filter((item) => {
      if (query) {
        const keyword = query.toLowerCase();
        const matched =
          item.title.toLowerCase().includes(keyword) ||
          item.cast.some((actor) => actor.toLowerCase().includes(keyword)) ||
          item.characters.some(
            (char) =>
              char.name.toLowerCase().includes(keyword) ||
              char.actor.toLowerCase().includes(keyword)
          );
        if (!matched) return false;
      }
      if (filter.era) {
        const era = seriesEras.find((entry) => entry.label === filter.era);
        if (era && (item.year < era.min || item.year > era.max)) return false;
      }
      if (filter.categories.length > 0 && !filter.categories.includes(item.category)) {
        return false;
      }
      if (filter.regions.length > 0 && !filter.regions.includes(item.region)) {
        return false;
      }
      return true;
    });

    return [...list].sort((a, b) => {
      if (filter.sort === "year") return b.year - a.year;
      if (filter.sort === "rating") return b.rating - a.rating;
      return b.hot - a.hot;
    });
  }, [filter, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleFilterChange = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  const resetAll = () => {
    setFilter(defaultFilter);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">电视剧分类</span>
            </nav>
            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              仙剑奇侠传电视剧分类
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              汇总仙剑系列正传、续集、衍生作品及经典相关仙侠剧。通过年代、副分类与地区筛选，
              结合热度、首播时间与评分排序，帮你快速定位想看的那一部。
            </p>
            {query && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-50 px-3.5 py-1.5 text-xs text-primary-700">
                <i className="ri-search-line"></i>
                搜索关键词：{query}
              </p>
            )}
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
          <div className="flex flex-col gap-8 lg:flex-row">
            <aside className="hidden w-64 shrink-0 lg:block">
              <div className="sticky top-28 rounded-lg border border-background-200 bg-background-50 p-5">
                <FilterPanel
                  value={filter}
                  onChange={handleFilterChange}
                  onReset={resetAll}
                  total={filtered.length}
                />
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              <div className="mb-5 flex items-center justify-between gap-3">
                <p className="text-sm text-foreground-600">
                  共找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 部剧集
                </p>
                <button
                  type="button"
                  onClick={() => setPanelOpen(true)}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 px-4 py-2 text-sm font-medium text-primary-700 lg:hidden"
                >
                  <i className="ri-equalizer-line"></i>
                  筛选与排序
                </button>
              </div>

              {pageItems.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5">
                    {pageItems.map((item) => (
                      <SeriesCard key={item.id} series={item} />
                    ))}
                  </div>
                  <Pagination current={safePage} total={totalPages} onChange={setPage} />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-emotion-sad-line text-2xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                    没有符合条件的剧集
                  </h3>
                  <p className="mt-2 max-w-sm text-sm text-foreground-600">
                    试试调整筛选条件，或者重置后看看全部仙侠剧集。
                  </p>
                  <button
                    type="button"
                    onClick={resetAll}
                    className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {panelOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setPanelOpen(false)}
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">筛选与排序</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setPanelOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-background-100"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>
            <FilterPanel
              value={filter}
              onChange={handleFilterChange}
              onReset={resetAll}
              total={filtered.length}
            />
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="mt-6 w-full whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}